import React, { Component } from 'react';
import {
    Panel,
    Grid,
    Row,
    Col,
    Button,
    Table,
    Modal,
    FormGroup,
    ControlLabel,
    FormControl,
    Alert
} from "react-bootstrap";
import Card from "components/Card/Card.jsx";
import { isAdmin } from "helpers/permissions";
import firebase from "../../../config/firebase";

class AdminTrainingManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            trainings: [],
            loading: false,
            error: null,
            showModal: false,
            editMode: false,
            selectedTraining: null,
            formData: {
                title: '',
                summary: '',
                category: 'course',
                contentType: 'pdf',
                price: '',
                youtubeUrl: '',
                startDate: '',
                startTime: '',
                duration: '',
                isActive: true
            }
        };
    }

    async componentDidMount() {
        // Vérifier les permissions
        const user = firebase.auth().currentUser;
        if (!user || !await isAdmin(user)) {
            this.setState({ error: "Accès non autorisé" });
            return;
        }

        this.loadTrainings();
    }

    loadTrainings = async () => {
        try {
            this.setState({ loading: true });
            const db = firebase.firestore();
            const trainingsSnapshot = await db.collection('trainings').orderBy('createdAt', 'desc').get();

            const trainings = [];
            trainingsSnapshot.forEach(doc => {
                trainings.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            this.setState({ trainings, loading: false });
        } catch (error) {
            this.setState({
                error: 'Erreur lors du chargement des formations: ' + error.message,
                loading: false
            });
        }
    };

    handleCreateTraining = () => {
        this.setState({
            showModal: true,
            editMode: false,
            selectedTraining: null,
            formData: {
                title: '',
                summary: '',
                category: 'course',
                contentType: 'pdf',
                price: '',
                youtubeUrl: '',
                startDate: '',
                startTime: '',
                duration: '',
                isActive: true
            }
        });
    };

    handleEditTraining = (training) => {
        this.setState({
            showModal: true,
            editMode: true,
            selectedTraining: training,
            formData: {
                title: training.title || '',
                summary: training.summary || '',
                category: training.category || 'course',
                contentType: training.contentType || 'pdf',
                price: training.price ? training.price.toString() : '',
                youtubeUrl: training.youtubeUrl || '',
                startDate: training.startDate || '',
                startTime: training.startTime || '',
                duration: training.duration || '',
                isActive: training.isActive !== false
            }
        });
    };

    handleFormChange = (field, value) => {
        this.setState({
            formData: {
                ...this.state.formData,
                [field]: value
            }
        });
    };

    handleSaveTraining = async () => {
        try {
            const { formData, selectedTraining, editMode } = this.state;
            const db = firebase.firestore();

            // Validation
            if (!formData.title.trim()) {
                alert('Le titre est requis');
                return;
            }

            const trainingData = {
                ...formData,
                price: parseFloat(formData.price) || 0,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            };

            if (editMode && selectedTraining) {
                // Mise à jour
                await db.collection('trainings').doc(selectedTraining.id).update(trainingData);
            } else {
                // Création
                trainingData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
                await db.collection('trainings').add(trainingData);
            }

            this.setState({ showModal: false });
            this.loadTrainings();
        } catch (error) {

            alert('Erreur lors de la sauvegarde: ' + error.message);
        }
    };

    handleDeleteTraining = async (trainingId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
            return;
        }

        try {
            const db = firebase.firestore();
            await db.collection('trainings').doc(trainingId).delete();
            this.loadTrainings();
        } catch (error) {

            alert('Erreur lors de la suppression: ' + error.message);
        }
    };

    toggleTrainingStatus = async (training) => {
        try {
            const db = firebase.firestore();
            await db.collection('trainings').doc(training.id).update({
                isActive: !training.isActive,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            this.loadTrainings();
        } catch (error) {

            alert('Erreur lors du changement de statut: ' + error.message);
        }
    };

    closeModal = () => {
        this.setState({ showModal: false });
    };

    getCategoryLabel = (category) => {
        const labels = {
            'course': 'Modules',
            'td': 'Cas Pratiques',
            'referentiels': 'Référentiels',
            'infos_alertes': 'Infos et Alertes',
            'tp': 'Autres'
        };
        return labels[category] || category;
    };

    getCategoryColor = (category) => {
        const colors = {
            'course': '#3b82f6',      // Bleu
            'td': '#10b981',          // Vert
            'referentiels': '#3b82f6', // Bleu
            'infos_alertes': '#10b981', // Vert
            'tp': '#6b7280'           // Gris (blanc/neutre)
        };
        return colors[category] || '#6b7280';
    };

    getContentTypeLabel = (contentType) => {
        const labels = {
            'pdf': 'PDF',
            'video': 'Vidéo',
            'book': 'Livre'
        };
        return labels[contentType] || contentType;
    };

    render() {
        const { trainings, loading, error, showModal, formData, editMode } = this.state;

        if (error) {
            return (
                <div className="content">
                    <Alert bsStyle="danger">
                        <strong>Erreur:</strong> {error}
                    </Alert>
                </div>
            );
        }

        return (
            <div className="content admin-training-page">
                <div className="admin-training-header">
                    <div>
                        <span className="admin-training-eyebrow">Administration</span>
                        <h1>Formations</h1>
                        <p>Créez et gérez les contenus de formation proposés aux utilisateurs.</p>
                    </div>
                    <Button
                        className="admin-training-create"
                        onClick={this.handleCreateTraining}
                        disabled={loading}
                    >
                        <i className="pe-7s-plus" /> Nouvelle formation
                    </Button>
                </div>
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            <Card
                                content={
                                    <>
                                        {/* LOADING */}
                                        {loading ? (
                                            <div className="text-center py-4">
                                                <i className="fa fa-spinner fa-spin" /> Chargement...
                                            </div>
                                        ) : (
                                            <Table className="admin-training-table" striped hover responsive>
                                                <thead>
                                                <tr>
                                                    <th>Titre</th>
                                                    <th>Catégorie</th>
                                                    <th>Type</th>
                                                    <th>Prix</th>
                                                    <th>Statut</th>
                                                    <th>Actions</th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {trainings.map((training) => (
                                                    <tr key={training.id} style={{ height: 65 }}>
                                                        {/* TITRE */}
                                                        <td style={{ verticalAlign: 'middle',
                                                            fontSize: 16}}>
                                                            <strong>{training.title}</strong>
                                                            <br />
                                                            <small className="text-muted" style={{
                                                                fontSize: 14}}>
                                                                {training.summary}
                                                            </small>
                                                        </td>

                                                        {/* CATEGORIE */}
                                                                                        <td style={{ verticalAlign: 'middle' }}>
                                                          <span
                                                              style={{
                                                                  color: this.getCategoryColor(training.category),
                                                                  backgroundColor: `${this.getCategoryColor(training.category)}15`,
                                                                  padding: '5px 10px',
                                                                  borderRadius: 12,
                                                                  fontWeight: 600,
                                                                  fontSize: 14
                                                              }}
                                                          >
                                                            {this.getCategoryLabel(training.category)}
                                                          </span>
                                                        </td>

                                                        {/* TYPE */}
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            {this.getContentTypeLabel(training.contentType)}
                                                        </td>

                                                        {/* PRIX */}
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            {training.price ? (
                                                                <span>{training.price} €</span>
                                                            ) : (
                                                                <span style={{ color: 'green', fontWeight: 600 }}>
                                                              Gratuit
                                                            </span>
                                                            )}
                                                        </td>

                                                        {/* STATUS */}
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            <Button
                                                                bsStyle={training.isActive ? 'success' : 'warning'}
                                                                bsSize="small"
                                                                onClick={() => this.toggleTrainingStatus(training)}
                                                            >
                                                                {training.isActive ? 'Actif' : 'Inactif'}
                                                            </Button>
                                                        </td>

                                                        {/* ACTIONS */}
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            <div style={{ display: 'flex', gap: 8 }}>
                                                                <Button
                                                                    bsStyle="info"
                                                                    bsSize="small"
                                                                    onClick={() => this.handleEditTraining(training)}
                                                                >
                                                                    <i className="pe-7s-note" /> Détail
                                                                </Button>

                                                                <Button
                                                                    bsStyle="danger"
                                                                    bsSize="small"
                                                                    onClick={() => this.handleDeleteTraining(training.id)}
                                                                >
                                                                    <i className="pe-7s-trash" /> Supprimer
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}

                                                {trainings.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">
                                                            Aucune formation trouvée
                                                        </td>
                                                    </tr>
                                                )}
                                                </tbody>
                                            </Table>
                                        )}
                                    </>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>

                {/* MODAL */}
                <Modal show={showModal} onHide={this.closeModal} bsSize="lg" dialogClassName="admin-training-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {editMode ? 'Modifier la formation' : 'Nouvelle formation'}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <form>
                            <FormGroup>
                                <ControlLabel>Titre *</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={formData.title}
                                    placeholder="Titre de la formation"
                                    onChange={(e) =>
                                        this.handleFormChange('title', e.target.value)
                                    }
                                />
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Résumé</ControlLabel>
                                <FormControl
                                    componentClass="textarea"
                                    rows={3}
                                    value={formData.summary}
                                    placeholder="Description de la formation"
                                    onChange={(e) =>
                                        this.handleFormChange('summary', e.target.value)
                                    }
                                />
                            </FormGroup>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Catégorie</ControlLabel>
                                        <FormControl
                                            componentClass="select"
                                            value={formData.category}
                                            onChange={(e) =>
                                                this.handleFormChange('category', e.target.value)
                                            }
                                        >
                                            <option value="course">Modules</option>
                                            <option value="td">Cas Pratiques</option>
                                            <option value="referentiels">Référentiels</option>
                                            <option value="infos_alertes">Infos et Alertes</option>
                                            <option value="tp">Autres</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>

                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Type de contenu</ControlLabel>
                                        <FormControl
                                            componentClass="select"
                                            value={formData.contentType}
                                            onChange={(e) =>
                                                this.handleFormChange('contentType', e.target.value)
                                            }
                                        >
                                            <option value="pdf">PDF</option>
                                            <option value="video">Vidéo</option>
                                            <option value="book">Livre</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Prix (€)</ControlLabel>
                                        <FormControl
                                            type="number"
                                            value={formData.price}
                                            onChange={(e) =>
                                                this.handleFormChange('price', e.target.value)
                                            }
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={6}>
                                    <FormGroup>
                                        <ControlLabel>Durée</ControlLabel>
                                        <FormControl
                                            type="text"
                                            value={formData.duration}
                                            placeholder="ex: 2h30"
                                            onChange={(e) =>
                                                this.handleFormChange('duration', e.target.value)
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup>
                                <ControlLabel>URL YouTube</ControlLabel>
                                <FormControl
                                    type="url"
                                    value={formData.youtubeUrl}
                                    placeholder="https://youtube.com/watch?v=..."
                                    onChange={(e) =>
                                        this.handleFormChange('youtubeUrl', e.target.value)
                                    }
                                />
                            </FormGroup>
                        </form>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={this.closeModal}>Annuler</Button>
                        <Button bsStyle="primary" onClick={this.handleSaveTraining}>
                            {editMode ? 'Mettre à jour' : 'Créer'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style jsx>{`
                    .admin-training-page {
                        min-height: 100vh;
                        padding: 24px;
                        background: #f6f9fa;
                    }
                    .admin-training-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 20px;
                        max-width: 1500px;
                        margin: 0 auto 20px;
                        padding: 22px 28px;
                        background: linear-gradient(120deg, #fff 0%, #f2fbfd 100%);
                        border: 1px solid #e4eef1;
                        border-radius: 15px;
                        box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                    }
                    .admin-training-eyebrow {
                        display: block;
                        margin-bottom: 4px;
                        color: #64748b;
                        font-size: 11px;
                        font-weight: 700;
                        letter-spacing: 1px;
                        text-transform: uppercase;
                    }
                    .admin-training-header h1 {
                        margin: 0;
                        color: #17313d;
                        font-size: 26px;
                        font-weight: 700;
                    }
                    .admin-training-header p {
                        margin: 6px 0 0;
                        color: #64748b;
                        font-size: 13px;
                    }
                    .admin-training-page .admin-training-create,
                    .admin-training-page .admin-training-create:hover,
                    .admin-training-page .admin-training-create:focus {
                        min-height: 42px;
                        padding: 10px 16px;
                        color: #073b46;
                        background: var(--app-primary-color);
                        border: 1px solid var(--app-primary-color);
                        border-radius: 8px;
                        font-weight: 600;
                        box-shadow: 0 4px 12px rgba(var(--app-primary-rgb), 0.18);
                    }
                    .admin-training-page .card {
                        border: 1px solid #e3ebee;
                        border-radius: 13px;
                        box-shadow: 0 7px 22px rgba(31, 58, 69, 0.05);
                    }
                    .admin-training-page .admin-training-table {
                        margin-bottom: 0;
                        font-size: 14px;
                    }
                    .admin-training-page .admin-training-table > thead > tr > th {
                        padding: 12px 14px;
                        color: #405563;
                        background: #f3f8f9;
                        border-bottom: 1px solid #e3ebee;
                        font-size: 11px;
                        letter-spacing: 0.4px;
                        text-transform: uppercase;
                        white-space: nowrap;
                    }
                    .admin-training-page .admin-training-table > tbody > tr > td {
                        padding: 12px 14px;
                        color: #475569;
                        vertical-align: middle;
                    }
                    .admin-training-page button:focus-visible {
                        outline: 3px solid rgba(var(--app-primary-rgb), 0.28);
                        outline-offset: 2px;
                    }
                    :global(.admin-training-modal) {
                        width: 1400px !important;
                    }
                    :global(.admin-training-modal .modal-content) {
                        border-radius: 12px !important;
                        padding: 10px !important;
                    }
                    @media (max-width: 1500px) {
                        :global(.admin-training-modal) {
                            width: 95vw !important;
                        }
                    }
                    @media (max-width: 640px) {
                        .admin-training-page { padding: 16px 12px; }
                        .admin-training-header { align-items: flex-start; flex-direction: column; padding: 20px; }
                        .admin-training-page .admin-training-create { width: 100%; }
                    }
                `}</style>
            </div>
        );
    }
}

export default AdminTrainingManagement;
